import React, { useState } from "react";
import { Typography, Button } from "antd";

import { Value } from "@components/Text";
import { useAppContext } from "@components/AppContext";
import type { AppContext, Operation } from "@components/AppContext/types";

type Props = {
  mobileNumber: string;
  enableOperation: Operation;
  setVerificationRequestId: (verificationRequestId: string) => void;
}

type CreateVerificationRequestResponse = {
  data: {
    verificationRequestId: string;
  };
}

type RequestError = {
  code?: string;
}

const LABEL_DELAY = "Verification code could be send in 1 minute";
const LABEL_SUBMIT = "Send verification code";
const LABEL_MOBILE = "Mobile number";
const LABEL_DESCRIPTION = "To confirm your mobile number, we'll be sending you a verification code through SMS.";

const CreateVerificationRequest = ({
  mobileNumber,
  enableOperation,
  setVerificationRequestId,
}: Props) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const { request, showErrorMessage } = useAppContext() as AppContext;

  const createVerificationRequest = async () => {
    let verificationRequestId = "";

    try {
      const { data } = await request<CreateVerificationRequestResponse>(
        enableOperation,
        {},
        [ "UnprocessibleConditionError" ],
      );
      verificationRequestId = data.verificationRequestId;

    } catch (error) {
      const requestError = error as RequestError;
      const isExpected = requestError.code === "UnprocessibleConditionError";

      if (!isExpected) {
        console.error(error);
        return;
      }

      showErrorMessage(LABEL_DELAY);
      return;
    }

    setVerificationRequestId(verificationRequestId);
  };

  const onClick = async () => {
    setIsLoading(true);
    await createVerificationRequest();
    setIsLoading(false);
  };

  return (
    <>
      <Typography.Paragraph>
        {LABEL_DESCRIPTION}
      </Typography.Paragraph>

      <Typography.Paragraph>
        {`${LABEL_MOBILE}: `}
        <Typography.Text strong>
          <Value value={mobileNumber} />
        </Typography.Text>
      </Typography.Paragraph>

      <Button
        size="large"
        type="primary"
        loading={isLoading}
        onClick={onClick}
      >
        {LABEL_SUBMIT}
      </Button>
    </>
  );
};

export default CreateVerificationRequest;
